import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    IconButton, Menu, MenuItem, Dialog, DialogActions, 
    DialogContent, DialogContentText, DialogTitle, Button, Box, Typography,
    TextField
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { getProducts, deleteProduct, updateProduct } from '../Service/productServices';
import TopHeader from '../../common/Header/TopHeader';
import ProductsKPI from './ProductsKPI';
import ProductsFilter from './ProductsFilter';
import AddProductDrawer from './AddProductDrawer';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ProductsView = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const [actionRow, setActionRow] = useState(null);
    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [showKPIs, setShowKPIs] = useState(true);
    const [editingDescription, setEditingDescription] = useState(null);
    const [editingDescriptionValue, setEditingDescriptionValue] = useState('');

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const filters = {};
            if (typeFilter !== 'all') filters.type = typeFilter;
            if (categoryFilter) filters.category = categoryFilter;
            if (activeFilter !== 'all') filters.isActive = activeFilter === 'active';
            if (searchTerm) filters.search = searchTerm;
            
            const data = await getProducts(filters);
            const productsArray = Array.isArray(data) ? data : [];
            setProducts(productsArray);
            setFilteredProducts(productsArray);
        } catch (err) {
            console.error('Error al obtener los productos', err);
            setProducts([]);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    }, [typeFilter, categoryFilter, activeFilter, searchTerm]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleEdit = (product) => {
        setSelectedProduct(product);
        setEditDrawerOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this product?');
        if (confirmDelete) {
            try {
                await deleteProduct(id);
                fetchProducts();
            } catch (err) {
                console.error('Error al eliminar producto:', err);
            }
        }
    };

    const handleBulkDelete = async () => {
        const ids = Object.keys(rowSelection).map(index => filteredProducts[index]._id);
        for (let id of ids) {
            await deleteProduct(id);
        }
        setDeleteConfirmOpen(false);
        setRowSelection({});
        fetchProducts();
    };

    const handleProductAdded = () => {
        fetchProducts();
        setAddDrawerOpen(false);
        setEditDrawerOpen(false);
        setSelectedProduct(null);
    };

    const handleCloseDrawer = () => {
        setAddDrawerOpen(false);
        setEditDrawerOpen(false);
        setSelectedProduct(null);
    };

    const handleDescriptionDoubleClick = (product) => {
        setEditingDescription(product._id);
        setEditingDescriptionValue(product.description || '');
    };

    const handleDescriptionSave = async (productId) => {
        try {
            await updateProduct(productId, { description: editingDescriptionValue });
            setEditingDescription(null);
            setEditingDescriptionValue('');
            fetchProducts();
        } catch (err) {
            console.error('Error updating description:', err);
            alert('Error updating description');
        }
    };

    const handleDescriptionCancel = () => {
        setEditingDescription(null);
        setEditingDescriptionValue('');
    };

    const exportCSV = () => {
        const headers = ['Code', 'Name', 'Type', 'Category', 'Price', 'Cost', 'Stock', 'Active'];
        const rows = filteredProducts.map(p => [
            p.code,
            p.name,
            p.type,
            p.category,
            p.price,
            p.cost || '',
            p.stock || '',
            p.isActive ? 'Yes' : 'No'
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'products.csv';
        a.click();
    };

    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredProducts.map(p => ({
            Code: p.code,
            Name: p.name,
            Type: p.type,
            Category: p.category,
            Price: p.price,
            Cost: p.cost || '',
            Stock: p.stock || '',
            Active: p.isActive ? 'Yes' : 'No'
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Products');
        XLSX.writeFile(wb, 'products.xlsx');
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        const headers = [['Code', 'Name', 'Type', 'Category', 'Price', 'Active']];
        const data = filteredProducts.map(p => [
            p.code,
            p.name,
            p.type,
            p.category,
            `$${p.price?.toFixed(2)}`,
            p.isActive ? 'Yes' : 'No'
        ]);
        doc.autoTable({ head: headers, body: data });
        doc.save('products.pdf');
    };

    const handleRemoveFilter = (filterKey) => {
        switch(filterKey) {
            case 'type':
                setTypeFilter('all');
                break;
            case 'category':
                setCategoryFilter('');
                break;
            case 'status':
                setActiveFilter('all');
                break;
            case 'search':
                setSearchTerm('');
                break;
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        setTypeFilter('all');
        setCategoryFilter('');
        setActiveFilter('all');
        setSearchTerm('');
        setRowSelection({});
    };

    const handleClearSelection = () => {
        setRowSelection({});
    };

    const activeFiltersArray = useMemo(() => {
        const filters = [];

        if (typeFilter !== 'all') {
            filters.push({
                label: 'Type',
                value: typeFilter === 'consumible' ? 'Consumibles' : 'Services',
                key: 'type'
            });
        }

        if (categoryFilter) {
            filters.push({
                label: 'Category',
                value: categoryFilter,
                key: 'category'
            });
        }

        if (activeFilter !== 'all') {
            filters.push({
                label: 'Status',
                value: activeFilter === 'active' ? 'Active' : 'Inactive',
                key: 'status'
            });
        }

        if (searchTerm) {
            filters.push({
                label: 'Search',
                value: searchTerm,
                key: 'search'
            });
        }

        const selectedCount = Object.keys(rowSelection).length;
        if (selectedCount > 0) {
            filters.push({
                label: 'Selected',
                value: selectedCount,
                key: 'selected'
            });
        }

        return filters;
    }, [typeFilter, categoryFilter, activeFilter, searchTerm, rowSelection]);

    const totalProducts = Array.isArray(products) ? products.length : 0;
    const consumibles = Array.isArray(products) ? products.filter(p => p.type === 'consumible').length : 0;
    const services = Array.isArray(products) ? products.filter(p => p.type === 'service').length : 0;
    const activeProducts = Array.isArray(products) ? products.filter(p => p.isActive).length : 0;

    const columns = useMemo(() => [
        {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ row }) => {
                const [anchorEl, setAnchorEl] = useState(null);
                const open = Boolean(anchorEl);

                const handleClick = (event) => {
                    event.stopPropagation();
                    setAnchorEl(event.currentTarget);
                };

                const handleClose = () => {
                    setAnchorEl(null);
                };

                const handleMenuAction = (action) => {
                    handleClose();
                    action();
                };

                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        <IconButton onClick={handleClick}>
                            <p>...</p>
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                            <MenuItem onClick={() => handleMenuAction(() => {
                                setActionRow(row.original);
                                setDetailsOpen(true);
                            })}>
                                View Details
                            </MenuItem>
                            {/* <MenuItem onClick={() => handleMenuAction(() => handleEdit(row.original))}>
                                Edit
                            </MenuItem> */}
                            <MenuItem onClick={() => handleMenuAction(() => window.print())}>
                                Print
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuAction(() => handleDelete(row.original._id))}>
                                Delete
                            </MenuItem>
                        </Menu>
                    </div>
                );
            },
            size: 50,
        },
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => {
                const { name, imageUrl } = row.original;
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                        {imageUrl ? (
                            <img
                                src={imageUrl}
                                alt={name}
                                className="rounded-circle me-2"
                                style={{ width: 30, height: 30, objectFit: "cover" }}
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        ) : (
                            <div
                                className="rounded-circle me-2"
                                style={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "#ccc",
                                }}
                            />
                        )}
                        <span className="text-truncate">{name}</span>
                    </div>
                );
            },
        },
        { 
            accessorKey: 'code', 
            header: 'Code',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center text-truncate" style={{ height: '60px' }}>
                    {row.original.code}
                </div>
            )
        },
        { 
            accessorKey: 'type', 
            header: 'Type',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    <span className={`badge ${row.original.type === 'service' ? 'bg-info' : 'bg-success'}`}>
                        {row.original.type === 'service' ? 'Service' : 'Consumible'}
                    </span>
                </div>
            )
        },
        { 
            accessorKey: 'category', 
            header: 'Category',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center text-truncate" style={{ height: '60px' }}>
                    {row.original.serviceType}
                </div>
            )
        },
        { 
            accessorKey: 'description', 
            header: 'Description',
            Cell: ({ row }) => {
                const isEditing = editingDescription === row.original._id;
                
                return (
                    <div className="d-flex align-items-center" style={{ height: '60px', maxWidth: '300px' }}>
                        {isEditing ? (
                            <div className="d-flex align-items-center gap-2 w-100">
                                <TextField
                                    size="small"
                                    value={editingDescriptionValue}
                                    onChange={(e) => setEditingDescriptionValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleDescriptionSave(row.original._id);
                                        } else if (e.key === 'Escape') {
                                            handleDescriptionCancel();
                                        }
                                    }}
                                    autoFocus
                                    fullWidth
                                    multiline
                                />
                                <IconButton 
                                    size="small" 
                                    color="primary"
                                    onClick={() => handleDescriptionSave(row.original._id)}
                                >
                                    ✓
                                </IconButton>
                                <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={handleDescriptionCancel}
                                >
                                    ✕
                                </IconButton>
                            </div>
                        ) : (
                            <span 
                                className="text-truncate d-inline-block" 
                                style={{ 
                                    maxWidth: '100%',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s'
                                }}
                                onDoubleClick={() => handleDescriptionDoubleClick(row.original)}
                                title="Double-click to edit"
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                {row.original.description || 'No description'}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'price',
            header: 'Price',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {`$${row.original.price?.toFixed(2)}`}
                </div>
            )
        },
        { 
            accessorKey: 'stock', 
            header: 'Stock',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    {row.original.type === 'consumible' ? row.original.stock : 'N/A'}
                </div>
            )
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center" style={{ height: '60px' }}>
                    <span className={`badge ${row.original.isActive ? 'bg-success' : 'bg-danger'}`}>
                        {row.original.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>
            )
        }
    ], [editingDescription, editingDescriptionValue]);

    return (
        <main id="app">
            <TopHeader />

            <div className="content_wrapper">
                <section className="doctor-management-wrapper bg-white pb-5">
                    <div className="container-fluid px-lg-5">

                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Products</li>
                            </ol>
                        </nav>

                        <ProductsKPI 
                            totalProducts={totalProducts}
                            consumibles={consumibles}
                            services={services}
                            activeProducts={activeProducts}
                            showKPIs={showKPIs}
                            setShowKPIs={setShowKPIs}
                        />

                        <div className="filter-container py-3">
                            <div className="row align-items-center">
                                <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                                    <div className="position-relative">
                                        <div className="input-group">
                                            <button type="submit" className="p-0 bg-transparent border-0">
                                                <span className="d-block material-symbols-outlined icon-lg">search</span>
                                            </button>
                                            <input 
                                                type="text"
                                                className="form-control ps-2 bg-transparent border-0 shadow-none"
                                                placeholder="Search products..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-2">
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn btn btn-gray dropdown-toggle d-flex align-items-center gap-1"
                                            type="button"
                                            onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                                        >
                                            <span className="d-block">Action</span>
                                        </button>
                                        <Menu
                                            anchorEl={actionsAnchorEl}
                                            open={Boolean(actionsAnchorEl)}
                                            onClose={() => setActionsAnchorEl(null)}
                                        >
                                            <MenuItem onClick={() => {
                                                exportCSV();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export CSV
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportExcel();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export Excel
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                exportPDF();
                                                setActionsAnchorEl(null);
                                            }}>
                                                Export PDF
                                            </MenuItem>
                                            <MenuItem onClick={() => {
                                                setDeleteConfirmOpen(true);
                                                setActionsAnchorEl(null);
                                            }}>
                                                Delete Selected
                                            </MenuItem>
                                        </Menu>
                                    </div>

                                    <ProductsFilter
                                        typeFilter={typeFilter}
                                        setTypeFilter={setTypeFilter}
                                        categoryFilter={categoryFilter}
                                        setCategoryFilter={setCategoryFilter}
                                        activeFilter={activeFilter}
                                        setActiveFilter={setActiveFilter}
                                        categories={[...new Set(products.map(p => p.category).filter(Boolean))]}
                                    />
                                    
                                    <button 
                                        className="btn btn-primary d-flex align-items-center gap-1" 
                                        onClick={() => setAddDrawerOpen(true)}
                                    >
                                        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {activeFiltersArray.length > 0 && (
                            <div className="container-fluid mb-3 mt-3 border rounded p-3 bg-light">
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        <span className="text-muted me-2 fw-bold">Active Filters:</span>
                                        {activeFiltersArray.map((filter, index) => (
                                            <span 
                                                key={index} 
                                                className="badge bg-white text-dark border me-2 mb-2 d-inline-flex align-items-center"
                                                style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                                            >
                                                <strong>{filter.label}:</strong> 
                                                <span className="ms-1">{filter.value}</span>
                                                <button 
                                                    className="btn btn-sm btn-link p-0 ms-2 text-danger"
                                                    onClick={() => {
                                                        if (filter.key === 'selected') {
                                                            handleClearSelection();
                                                        } else {
                                                            handleRemoveFilter(filter.key);
                                                        }
                                                    }}
                                                    style={{ textDecoration: 'none', fontSize: '1rem' }}
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="col-auto ms-auto">
                                        <button 
                                            className="btn btn-link text-primary fw-bold"
                                            onClick={handleClearAllFilters}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="table-wrapper">
                            <MaterialReactTable
                                columns={columns}
                                data={filteredProducts}
                                state={{ isLoading: loading, rowSelection }}
                                onRowSelectionChange={setRowSelection}
                                enableRowSelection={true}
                                enableColumnFilters
                                enableColumnOrdering
                                enableSorting
                                enablePagination
                            />
                        </div>
                    </div>
                </section>
            </div>

            {/* Add Product Drawer */}
            <AddProductDrawer
                open={addDrawerOpen}
                onClose={handleCloseDrawer}
                selectedProduct={null}
                onProductAdded={handleProductAdded}
            />

            {/* Edit Product Drawer */}
            <AddProductDrawer
                open={editDrawerOpen}
                onClose={handleCloseDrawer}
                selectedProduct={selectedProduct}
                onProductAdded={handleProductAdded}
            />

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected products?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleBulkDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Product Details</DialogTitle>
                <DialogContent dividers>
                    {actionRow && (
                        <Box>
                            <Typography variant="h6" className="mb-3">{actionRow.name}</Typography>
                            <div className="row">
                                <div className="col-md-6">
                                    <Typography><strong>Code:</strong> {actionRow.code}</Typography>
                                    <Typography><strong>Type:</strong> {actionRow.type}</Typography>
                                    <Typography><strong>Category:</strong> {actionRow.category}</Typography>
                                    <Typography><strong>Price:</strong> ${actionRow.price?.toFixed(2)}</Typography>
                                    {actionRow.type === 'consumible' && (
                                        <>
                                            <Typography><strong>Cost:</strong> ${actionRow.cost?.toFixed(2)}</Typography>
                                            <Typography><strong>Stock:</strong> {actionRow.stock}</Typography>
                                        </>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <Typography><strong>Status:</strong> {actionRow.isActive ? 'Active' : 'Inactive'}</Typography>
                                    {actionRow.type === 'service' && actionRow.serviceType && (
                                        <Typography><strong>Service Type:</strong> {actionRow.serviceType}</Typography>
                                    )}
                                    <Typography><strong>Description:</strong> {actionRow.description}</Typography>
                                </div>
                            </div>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </main>
    );
};

export default ProductsView;